import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { errorMessage } from '@/shared/services/api/api-error';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/shared/ui/cn';
import type { IconName } from '@/shared/ui/Icon';
import { Icon } from '@/shared/ui/Icon';

export type EmptyStateProps = {
  icon: IconName;
  title: ReactNode;
  /** Une phrase qui dit quoi faire, pas une excuse. */
  message?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
  /** Action sous forme de lien (ouvrir une page). */
  action?: ReactNode;
  /** Version resserrée, dans un bloc ou une colonne latérale. */
  compact?: boolean;
  className?: string;
};

/**
 * Ce qu'un écran montre quand il n'a rien : une icône au trait fin, un titre, une
 * phrase qui dit quoi faire, et au plus une action.
 */
export function EmptyState({
  icon,
  title,
  message,
  actionLabel,
  onAction,
  action,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center text-center',
        compact ? 'px-6 py-8' : 'px-8 py-10 md:py-16',
        className,
      )}
    >
      <Icon name={icon} size={compact ? 40 : 56} strokeWidth={1.2} className="text-fg-faint" />
      <h2 className={cn('mt-[18px] text-fg', compact ? 'text-title3' : 'text-title2')}>{title}</h2>
      {message ? (
        <p className="mt-1.5 max-w-[320px] text-subtle text-fg-muted">{message}</p>
      ) : null}
      {action ? (
        <div className="mt-[18px]">{action}</div>
      ) : actionLabel ? (
        <Button variant="primary" size="sm" className="mt-[18px]" onClick={onAction}>
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

export type ErrorStateProps = {
  /** L'échec remonté par React Query. */
  error: unknown;
  /** Titre de l'écran manqué : « Boutique introuvable », « Fil indisponible »… */
  title?: string;
  onRetry?: () => void;
  compact?: boolean;
  className?: string;
};

/**
 * Un chargement qui échoue se dit, et se retente. Sans lui, un écran de liste
 * retomberait sur son état vide (« le fil est encore vide » pour un serveur
 * injoignable), et une fiche resterait sur son squelette.
 */
export function ErrorState({ error, title, onRetry, compact, className }: ErrorStateProps) {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon="alert"
      title={title ?? t('state.loadFailed')}
      message={errorMessage(error)}
      actionLabel={onRetry ? t('action.retry') : undefined}
      onAction={onRetry}
      compact={compact}
      className={className}
    />
  );
}
